<template>
  <div class="home">
      <!-- header -->
      <section>
        <div class="py-8 px-8">
          <div class="d-flex flex-wrap align-items-center justify-content-between">
            <div class="col-12 col-lg-auto mb-3 mb-lg-0">
              <div class="d-flex align-items-center">
                <h4 class="mb-0 me-2">{{$t('leads')}}</h4>
              </div>
            </div>
            <div class="col-12 col-lg-auto d-flex align-items-center gap-2">
              <div class="input-group flex-grow-1" style="border-bottom: 1px solid #ddd;">
                <input class="form-control border-0 search-input" autocomplete="off" type="text" v-model="search" :placeholder="$t('search')">
              </div>
              <!-- Mobile filter toggle -->
              <button @click="showFilters = !showFilters" class="btn btn-outline-secondary d-md-none">
                <AppIcon name="bi-funnel" class="bi bi-funnel" />
              </button>
            </div>
          </div>
        </div>
      </section>
      <!-- header -->

      <!-- table -->
      <section class="">
        <div class="px-8">

          <!-- Mobile action buttons (always visible) -->
          <div class="d-flex d-md-none gap-2 mb-3">
            <button v-if="helper.checkAccess('create_leads') != -1" @click="create" class="btn btn-sm btn-success flex-grow-1">
              <PlusIcon class="me-1 client-lucide-icon" :size="15" :stroke-width="1.75" aria-hidden="true" />
              <span>{{$t('lead')}}</span>
            </button>
            
            <div class="dropdown">
              <button class="btn btn-sm btn-light" type="button" id="dropdownMenuButton" data-bs-toggle="dropdown" aria-expanded="false">
                <AppIcon name="bi-three-dots-vertical" class="bi bi-three-dots-vertical" />
              </button>
              <ul class="dropdown-menu dropdown-menu-end" aria-labelledby="dropdownMenuButton">
                <li v-if="helper.checkAccess('create_leads') != -1">
                  <a class="dropdown-item" href="#" @click.prevent="showUpload">
                    <AppIcon name="bi-upload" class="bi bi-upload me-2" />{{$t('import')}}
                  </a>
                </li>
                <li v-if="helper.checkAccess('create_leads') != -1"><hr class="dropdown-divider"></li>
                <li>
                  <button class="dropdown-item" type="button" :disabled="exporting || (!count && !selectedCount)" @click="exportLeads('csv')">
                    <AppIcon name="bi-filetype-csv" class="bi bi-filetype-csv me-2" />{{ $t('export_csv') }}
                  </button>
                </li>
                <li>
                  <button class="dropdown-item" type="button" :disabled="exporting || (!count && !selectedCount)" @click="exportLeads('xlsx')">
                    <AppIcon name="bi-file-earmark-excel" class="bi bi-file-earmark-excel me-2" />{{ $t('export_excel') }}
                  </button>
                </li>
              </ul>
            </div>
          </div>

          <!-- Filters section (collapsible on mobile, always visible on desktop) -->
          <div class="row align-items-end g-2 mb-4" :class="{'d-none d-md-flex': !showFilters}">
            
            <!-- Filter Dropdown (Shopify Style) -->
            <div class="col-12 col-md-auto">
              <div class="dropdown">
                <button class="btn btn-outline-secondary dropdown-toggle btn-sm" type="button" id="filterDropdown" data-bs-toggle="dropdown" aria-expanded="false">
                  <AppIcon name="bi-funnel" class="bi bi-funnel" /> {{$t('filter')}}
                  <span v-if="activeFilters.length" class="badge bg-secondary ms-1">{{activeFilters.length}}</span>
                </button>
                <button v-if="activeFilters.length" class="btn btn-link btn-sm text-danger text-decoration-none ms-2" type="button" @click.stop="clearAllFilters">
                  <AppIcon name="bi-x-circle" class="bi bi-x-circle me-1" />{{$t('clear_all')}}
                </button>
                <div class="dropdown-menu p-3" aria-labelledby="filterDropdown" style="min-width: 300px;" @click.stop>
                  
                  <!-- Active Filters -->
                  <div v-if="activeFilters.length > 0" class="mb-3">
                    <div v-for="(filter, index) in activeFilters" :key="index" class="mb-2">
                      <label class="form-label small mb-1">{{$t(filter.key)}}</label>
                      <div class="input-group input-group-sm">
                        <select v-if="filter.key === 'status' || filter.key === 'assigned_to' || (filter.key === 'source' && filterSourceOptions.length)" v-model="filter.value" class="form-select form-select-sm">
                          <option v-for="option in getFilterOptions(filter.key)" :key="option.value" :value="option.value">
                            {{option.label}}
                          </option>
                        </select>
                        <input v-else type="text" v-model="filter.value" class="form-control form-control-sm" :placeholder="$t(filter.key)">
                        <button class="btn btn-outline-danger" type="button" @click="removeFilter(index)">
                          <AppIcon name="bi-trash" class="bi bi-trash" />
                        </button>
                      </div>
                    </div>
                    <hr class="dropdown-divider">
                  </div>

                  <!-- Add Filter Options -->
                  <div class="d-grid gap-2">
                    <button v-for="filter in selectableFilters" :key="filter.key" 
                            class="btn btn-sm btn-outline-primary text-start" 
                            @click="addFilter(filter)">
                      <AppIcon name="bi-plus" class="bi bi-plus" /> {{$t(filter.key)}}
                    </button>
                  </div>

                  <!-- Apply and Clear -->
                  <div class="d-flex justify-content-between align-items-center mt-3">
                    <button class="btn btn-sm btn-success" @click="applyFilters">
                      {{$t('apply')}}
                    </button>
                    <button v-if="activeFilters.length > 0" class="btn btn-link btn-sm text-decoration-none text-danger p-0" @click="clearAllFilters">
                      {{$t('clear_all')}}
                    </button>
                  </div>
                </div>
              </div>
            </div>

            <!-- Spacer for desktop -->
            <div class="col d-none d-md-block"></div>
            
            <!-- Date picker -->
            <div class="col-12 col-md-3 col-lg-2">
              <label class="form-label small d-md-none mb-1">{{$t('filter_by_date')}}</label>
              <select v-model="dateRangeOption" @change="handleDateRangeChange" class="form-select form-select-sm">
                <option value="">{{$t('filter_by_date')}}</option>
                <option value="today">{{$t('today')}}</option>
                <option value="yesterday">{{$t('yesterday')}}</option>
                <option value="this_week">{{$t('this_week')}}</option>
                <option value="last_week">{{$t('last_week')}}</option>
                <option value="this_month">{{$t('this_month')}}</option>
                <option value="custom">{{$t('custom_date')}}</option>
              </select>
            </div>
            <div class="col-12 col-md-3 col-lg-2" v-if="dateRangeOption === 'custom'">
              <label class="form-label small d-md-none mb-1">{{$t('custom_date')}}</label>
              <date-picker :clearable="true" :editable="false" class="w-100" v-model="range" type="date" range :multi-calendars="1" :placeholder="$t('select_date_range')"></date-picker>
            </div>
            
            <!-- Desktop action buttons -->
            <div class="col-md-auto d-none d-md-flex gap-2">
              <div class="dropdown">
                <button
                  class="btn btn-sm btn-light"
                  type="button"
                  id="leadActionsDropdownDesktop"
                  data-bs-toggle="dropdown"
                  aria-expanded="false"
                >
                  <span v-if="exporting" class="spinner-border spinner-border-sm" role="status"></span>
                  <AppIcon name="bi-three-dots-vertical" v-else class="bi bi-three-dots-vertical" />
                </button>
                <ul class="dropdown-menu dropdown-menu-end" aria-labelledby="leadActionsDropdownDesktop">
                  <li v-if="helper.checkAccess('create_leads') != -1">
                    <a class="dropdown-item" href="#" @click.prevent="showUpload">
                      <AppIcon name="bi-upload" class="bi bi-upload me-2" />{{$t('import')}}
                    </a>
                  </li>
                  <li v-if="helper.checkAccess('create_leads') != -1"><hr class="dropdown-divider"></li>
                  <li>
                    <button class="dropdown-item" type="button" :disabled="exporting || (!count && !selectedCount)" @click="exportLeads('csv')">
                      <AppIcon name="bi-filetype-csv" class="bi bi-filetype-csv me-2" />{{ $t('export_csv') }}
                    </button>
                  </li>
                  <li>
                    <button class="dropdown-item" type="button" :disabled="exporting || (!count && !selectedCount)" @click="exportLeads('xlsx')">
                      <AppIcon name="bi-file-earmark-excel" class="bi bi-file-earmark-excel me-2" />{{ $t('export_excel') }}
                    </button>
                  </li>
                </ul>
              </div>
              <button v-if="helper.checkAccess('create_leads') != -1" @click="create" class="btn btn-sm btn-success px-4">
                <PlusIcon class="me-1 client-lucide-icon" :size="15" :stroke-width="1.75" aria-hidden="true" />
                <span>{{$t('lead')}}</span>
              </button>
            </div>
          </div>

          <div class="mb-6 bg-white rounded shadow">
            
            <!-- table -->
            <div class="page-table-container table-responsive leads-list-table">
              <table class="table mb-0 table-borderless table-striped small">
                <thead>
                  <tr class="text-secondary">
                    <th class="pt-4 pb-3 px-6" style="width: 40px;">
                      <div class="form-check mb-0">
                        <input class="form-check-input" type="checkbox" :checked="areAllSelected" @change="toggleSelectAll">
                      </div>
                    </th>
                    <sortable-table-header v-if="visibleColumns.reference" field="reference" label="reference" :sort-field="sortField" :sort-direction="sortDirection" @sort="applySort($event, getLeads)" />
                    <sortable-table-header v-if="visibleColumns.name" field="name" label="name" :sort-field="sortField" :sort-direction="sortDirection" @sort="applySort($event, getLeads)" />
                    <sortable-table-header v-if="visibleColumns.email" field="email" label="email" :sort-field="sortField" :sort-direction="sortDirection" @sort="applySort($event, getLeads)" />
                    <sortable-table-header v-if="visibleColumns.phone" field="phone" label="phone" :sort-field="sortField" :sort-direction="sortDirection" @sort="applySort($event, getLeads)" />
                    <sortable-table-header v-if="visibleColumns.company" field="company" label="company" :sort-field="sortField" :sort-direction="sortDirection" @sort="applySort($event, getLeads)" />
                    <sortable-table-header v-if="visibleColumns.city" field="city" label="city" :sort-field="sortField" :sort-direction="sortDirection" @sort="applySort($event, getLeads)" />
                    <sortable-table-header v-if="visibleColumns.address" field="address" label="address" :sort-field="sortField" :sort-direction="sortDirection" @sort="applySort($event, getLeads)" />
                    <sortable-table-header v-if="visibleColumns.source" field="source" label="source" :sort-field="sortField" :sort-direction="sortDirection" @sort="applySort($event, getLeads)" />
                    <sortable-table-header v-if="visibleColumns.status" field="status" label="status" :sort-field="sortField" :sort-direction="sortDirection" @sort="applySort($event, getLeads)" />
                    <sortable-table-header v-if="visibleColumns.assigned_to" field="assigned_to" label="assigned_to" :sort-field="sortField" :sort-direction="sortDirection" @sort="applySort($event, getLeads)" />
                    <sortable-table-header v-if="visibleColumns.value" field="value" label="value" :sort-field="sortField" :sort-direction="sortDirection" @sort="applySort($event, getLeads)" />
                    <sortable-table-header v-if="visibleColumns.created_at" field="created_at" label="created_at" :sort-field="sortField" :sort-direction="sortDirection" @sort="applySort($event, getLeads)" />
                    <sortable-table-header v-if="visibleColumns.updated_at" field="updated_at" label="updated_at" :sort-field="sortField" :sort-direction="sortDirection" @sort="applySort($event, getLeads)" />
                    <th v-if="visibleColumns.action" class="pt-4 pb-3 px-6">{{$t('action')}}</th>
                  </tr>
                </thead>
                <tbody>
                  <tr
                    v-for="lead in leads"
                    :key="lead.id"
                    :class="[leadReminderClass(lead), {'table-active': lead.selected}]"
                  >
                    <td class="py-5 px-6">
                      <div class="form-check mb-0">
                        <input class="form-check-input" type="checkbox" v-model="lead.selected" @change="changeSelectedItem">
                      </div>
                    </td>
                    <td v-if="visibleColumns.reference" class="py-5 px-6">
                      <router-link :to="`/leads/${lead.id}`"><b>{{lead.reference}}</b></router-link>
                      <span
                        v-if="getLeadReminder(lead.id)"
                        class="lead-reminder-badge ms-2"
                        :class="`lead-reminder-badge--${reminderUrgency(getLeadReminder(lead.id))}`"
                        :title="reminderTitle(getLeadReminder(lead.id))"
                      >
                        <AppIcon name="bi-bell-fill" class="bi bi-bell-fill me-1" />{{ formatReminderRelative(getLeadReminder(lead.id).remind_at) }}
                      </span>
                    </td>
                    <td v-if="visibleColumns.name" class="py-5 px-6">{{lead.name}}</td>
                    <td v-if="visibleColumns.email" class="py-5 px-6">{{lead.email}}</td>
                    <td v-if="visibleColumns.phone" class="py-5 px-6">{{lead.phone}}</td>
                    <td v-if="visibleColumns.company" class="py-5 px-6">{{lead.company}}</td>
                    <td v-if="visibleColumns.city" class="py-5 px-6">{{lead.city}}</td>
                    <td v-if="visibleColumns.address" class="py-5 px-6">{{lead.address}}</td>
                    <td v-if="visibleColumns.source" class="py-5 px-6">
                      <div v-if="configuredLeadSources.length && helper.checkAccess('edit_leads') != -1" class="dropdown">
                        <button
                          :id="`lead-source-${lead.id}`"
                          type="button"
                          class="badge bg-secondary border-0 dropdown-toggle"
                          data-bs-toggle="dropdown"
                          aria-expanded="false"
                          :disabled="sourceUpdatingLeadId === lead.id"
                        >
                          <span v-if="sourceUpdatingLeadId === lead.id" class="spinner-border spinner-border-sm me-1" role="status"></span>
                          {{ lead.source || $t('no_source') }}
                        </button>
                        <ul class="dropdown-menu shadow" :aria-labelledby="`lead-source-${lead.id}`">
                          <li>
                            <button type="button" class="dropdown-item" :class="{ active: !lead.source }" @click="changeLeadSource(lead, '')">
                              {{ $t('no_source') }}
                            </button>
                          </li>
                          <li v-for="source in leadSourceOptions(lead)" :key="source">
                            <button
                              type="button"
                              class="dropdown-item d-flex align-items-center justify-content-between gap-2"
                              :class="{ active: lead.source === source }"
                              @click="changeLeadSource(lead, source)"
                            >
                              <span>{{ source }}</span>
                              <AppIcon name="bi-check-lg" v-if="lead.source === source" class="bi bi-check-lg" />
                            </button>
                          </li>
                        </ul>
                      </div>
                      <span v-else class="badge bg-secondary">{{ lead.source || $t('no_source') }}</span>
                    </td>
                    <td v-if="visibleColumns.status" class="py-5 px-6">
                      <div v-if="statuses.length && helper.checkAccess('edit_leads') != -1" class="dropdown">
                        <button
                          :id="`lead-status-${lead.id}`"
                          type="button"
                          class="badge border-0 dropdown-toggle"
                          :style="{ backgroundColor: getStatusColor(lead.status), color: '#fff' }"
                          data-bs-toggle="dropdown"
                          aria-expanded="false"
                          :disabled="isLeadStatusDropdownDisabled(lead)"
                        >
                          <span v-if="statusUpdatingLeadId === lead.id" class="spinner-border spinner-border-sm me-1" role="status" aria-hidden="true"></span>
                          {{ getStatusName(lead.status) }}
                        </button>
                        <ul class="dropdown-menu shadow lead-status-menu" :aria-labelledby="`lead-status-${lead.id}`">
                          <li v-for="status in statuses" :key="status.id || status.name">
                            <button
                              type="button"
                              class="dropdown-item d-flex align-items-center justify-content-between gap-2"
                              :class="{ active: lead.status === status.name }"
                              @click="changeLeadStatus(lead, status.name)"
                            >
                              <span>{{ status.name }}</span>
                              <AppIcon name="bi-check-lg" v-if="lead.status === status.name" class="bi bi-check-lg" />
                            </button>
                          </li>
                        </ul>
                      </div>
                      <span v-else class="badge" :style="{ backgroundColor: getStatusColor(lead.status), color: '#fff' }">
                        {{ getStatusName(lead.status) }}
                      </span>
                    </td>
                    <td v-if="visibleColumns.assigned_to" class="py-5 px-6">
                      {{ lead.user ? formatUserLabel(lead.user) : $t('unassigned') }}
                    </td>
                    <td v-if="visibleColumns.value" class="py-5 px-6">{{ formatMoney(lead.value || 0) }}</td>
                    <td v-if="visibleColumns.created_at" class="py-5 px-6">{{formatDate(lead.created_at)}}</td>
                    <td v-if="visibleColumns.updated_at" class="py-5 px-6">{{formatDateTime(lead.updated_at)}}</td>
                    <td v-if="visibleColumns.action" class="py-5 px-6">
                      <button @click="goDetails(lead.id)" style="padding: 5px;margin: 0;font-size: 12px !important;margin-right:2px" class="btn btn-outline-primary btn-xs">
                        <AppIcon name="bi-eye" class="bi bi-eye" /> {{$t('details')}}
                      </button>
                      <button v-if="helper.checkAccess('edit_leads') != -1" @click="editRow(lead)" style="padding: 5px;margin: 0;font-size: 12px !important;margin-right:2px" class="btn btn-primary btn-xs">
                        <AppIcon name="bi-pencil" class="bi bi-pencil" /> {{$t('edit')}}
                      </button>
                      <button v-if="helper.checkAccess('delete_leads') != -1" @click="deleteRow(lead)" style="padding: 5px;margin: 0;font-size: 12px !important;" class="btn btn-primary btn-xs">
                        <AppIcon name="bi-trash" class="bi bi-trash" /> {{$t('delete')}}
                      </button>
                    </td>
                  </tr>
                </tbody>
              </table>

              <div v-if="loading === false && totalPages > 1" class="lead-pagination">
                <button
                  type="button"
                  class="btn pagination-btn"
                  :disabled="page <= 1"
                  @click="getLeads(page - 1)"
                  aria-label="Previous page"
                >
                  <AppIcon name="bi-chevron-left" class="bi bi-chevron-left" />
                </button>

                <template v-for="(currPage, index) in visiblePagination">
                  <span
                    v-if="currPage === '...'"
                    :key="'ellipsis-' + index"
                    class="pagination-ellipsis"
                  >
                    ...
                  </span>

                  <button
                    v-else
                    :key="'page-' + currPage"
                    type="button"
                    class="btn pagination-btn"
                    :class="{ active: Number(page) === Number(currPage) }"
                    @click="getLeads(currPage)"
                  >
                    {{ currPage }}
                  </button>
                </template>

                <button
                  type="button"
                  class="btn pagination-btn"
                  :disabled="page >= totalPages"
                  @click="getLeads(page + 1)"
                  aria-label="Next page"
                >
                  <AppIcon name="bi-chevron-right" class="bi bi-chevron-right" />
                </button>
              </div>
              <table-loading-bar v-if="loading" class="page-table-state leads-list-loading text-center" />
              <div v-if="loading === false && !leads.length && isFiltered" class="page-table-state leads-list-empty">{{$t('no_lead_found')}}</div>
              <div v-if="loading === false && !leads.length && !isFiltered" class="page-table-state leads-list-empty leads-list-empty--onboarding text-center">
                <AppIcon name="bi-people" class="bi bi-people text-muted" style="font-size: 3rem;" />
                <h4 class="mt-3">{{ $t('no_leads_yet') }}</h4>
                <p class="text-muted">{{ $t('start_by_adding_leads') }}</p>
                <button v-if="helper.checkAccess('create_leads') != -1" class="btn btn-outline-primary mt-2" @click="create">
                  {{ $t('add_first_lead') }}
                </button>
              </div>
            </div>
            <!-- table -->
          </div>

          <div class="col-sm-7">
            {{$t('show')}}
            <select v-model="per_page" class="form-select mb-3" name="Resulats" style="cursor:pointer;margin-right: 5px;width:100px;display:inline-block">
              <option :value="5">5</option>
              <option :value="10">10</option>
              <option :value="15">15</option>
              <option :value="25">25</option>
              <option :value="50">50</option>
              <option :value="100">100</option>
            </select>
            {{$t('entities')}}
          </div>

        </div>
      </section>
      <!-- table -->

      <!-- Bulk Action Bar -->
      <div v-if="isSelectedItem && (helper.checkAccess('edit_leads') != -1 || helper.checkAccess('delete_leads') != -1)" class="position-fixed bottom-0 start-50 translate-middle-x mb-4 p-3 bg-white shadow rounded d-flex flex-wrap align-items-center gap-3" style="z-index: 1050; border: 1px solid #dee2e6; min-width: 300px; max-width: calc(100vw - 2rem); justify-content: space-between;">
          <div class="d-flex align-items-center">
              <span class="fw-bold me-2">{{ selectedCount }}</span>
              <span class="text-muted">{{$t('selected')}}</span>
          </div>
          <div class="d-flex flex-wrap gap-2">
              <div v-if="helper.checkAccess('edit_leads') != -1" class="input-group input-group-sm" style="min-width: 280px;">
                  <select v-model="bulkAssigneeId" class="form-select" :disabled="bulkAssigning">
                      <option value="" disabled>{{$t('select_user')}}</option>
                      <option :value="null">{{$t('unassigned')}}</option>
                      <option v-for="user in users" :key="user.id" :value="user.id">{{formatUserLabel(user)}}</option>
                  </select>
                  <button class="btn btn-primary d-flex align-items-center" :disabled="bulkAssigneeId === '' || bulkAssigning" @click="assignSelected">
                      <span v-if="bulkAssigning" class="spinner-border spinner-border-sm me-1" role="status"></span>
                      <AppIcon name="bi-person-check" v-else class="bi bi-person-check me-1" /> {{$t('assign')}}
                  </button>
              </div>
              <div v-if="helper.checkAccess('edit_leads') != -1" class="input-group input-group-sm" style="min-width: 240px;">
                  <select v-model="bulkStatus" class="form-select" :disabled="bulkStatusUpdating">
                      <option value="" disabled>{{$t('status')}}</option>
                      <option v-for="status in statuses" :key="status.id || status.name" :value="status.name">{{status.name}}</option>
                  </select>
                  <button class="btn btn-primary d-flex align-items-center" :disabled="bulkStatus === '' || bulkStatusUpdating" @click="updateSelectedStatus">
                      <span v-if="bulkStatusUpdating" class="spinner-border spinner-border-sm me-1" role="status"></span>
                      <AppIcon name="bi-arrow-repeat" v-else class="bi bi-arrow-repeat me-1" /> {{$t('update')}}
                  </button>
              </div>
              <button v-if="helper.checkAccess('delete_leads') != -1" class="btn btn-danger btn-sm d-flex align-items-center" @click="deleteSelected">
                  <AppIcon name="bi-trash" class="bi bi-trash me-1" /> {{$t('delete')}}
              </button>
          </div>
      </div>

    <!-- Add/Edit Modal -->
    <div class="modal fade" id="lead-modal" tabindex="-1" aria-hidden="true">
      <div class="modal-dialog modal-lg">
        <div class="modal-content">
          <div class="modal-header border-0 pb-0">
            <h5 class="modal-title mb-0">
              <AppIcon name="bi-person-plus" class="bi bi-person-plus me-2" />
              {{ lead.id ? $t('edit_lead') : $t('add_lead') }}
            </h5>
            <button type="button" class="btn-close" data-bs-dismiss="modal" aria-label="close"></button>
          </div>
          <div class="modal-body pt-2" style="text-align:left">
            <div class="row g-3">
              <div class="col-md-6">
                <label class="form-label">{{$t('name')}} <span class="text-danger">*</span></label>
                <div class="input-group">
                  <span class="input-group-text bg-light border-end-0"><AppIcon name="bi-person" class="bi bi-person" /></span>
                  <input v-model="lead.name" type="text" class="form-control border-start-0 ps-0" :placeholder="$t('name')">
                </div>
              </div>
              <div class="col-md-6">
                <label class="form-label">{{$t('email')}}</label>
                <div class="input-group">
                  <span class="input-group-text bg-light border-end-0"><AppIcon name="bi-envelope" class="bi bi-envelope" /></span>
                  <input v-model="lead.email" type="email" class="form-control border-start-0 ps-0" :placeholder="$t('email')">
                </div>
              </div>
              <div class="col-md-6">
                <label class="form-label">{{$t('phone')}}</label>
                <div class="input-group">
                  <span class="input-group-text bg-light border-end-0"><AppIcon name="bi-telephone" class="bi bi-telephone" /></span>
                  <input v-model="lead.phone" type="tel" class="form-control border-start-0 ps-0" :placeholder="$t('phone')">
                </div>
              </div>
              <div class="col-md-6">
                <label class="form-label">{{$t('company')}}</label>
                <div class="input-group">
                  <span class="input-group-text bg-light border-end-0"><AppIcon name="bi-building" class="bi bi-building" /></span>
                  <input v-model="lead.company" type="text" class="form-control border-start-0 ps-0" :placeholder="$t('company')">
                </div>
              </div>
              <div class="col-md-6">
                <label class="form-label">{{$t('city')}}</label>
                <input v-model="lead.city" type="text" class="form-control" :placeholder="$t('city')">
              </div>
              <div class="col-md-6">
                <label class="form-label">{{$t('address')}}</label>
                <input v-model="lead.address" type="text" class="form-control" :placeholder="$t('address')">
              </div>
              <div class="col-md-6">
                <label class="form-label">{{$t('source')}}</label>
                <select v-if="configuredLeadSources.length" v-model="lead.source" class="form-select">
                  <option value="">{{$t('select_source')}}</option>
                  <option v-for="source in formSourceOptions" :key="source" :value="source">{{source}}</option>
                </select>
                <input v-else v-model="lead.source" type="text" class="form-control" :placeholder="$t('source')">
              </div>
              <div class="col-md-6">
                <label class="form-label">{{$t('status')}}</label>
                <select v-model="lead.status" class="form-select">
                  <option v-for="s in statuses" :key="s.id" :value="s.name">{{s.name}}</option>
                </select>
              </div>
              <div class="col-md-6">
                <label class="form-label">{{$t('value')}}</label>
                <div class="input-group">
                  <span class="input-group-text bg-light border-end-0"><AppIcon name="bi-currency-dollar" class="bi bi-currency-dollar" /></span>
                  <input v-model="lead.value" type="number" step="0.01" class="form-control border-start-0 ps-0" placeholder="0.00">
                </div>
              </div>
              <div class="col-md-6">
                <label class="form-label">{{$t('assigned_to')}}</label>
                <select v-model="lead.user_id" class="form-select">
                  <option :value="null">{{$t('unassigned')}}</option>
                  <option v-for="user in users" :key="user.id" :value="user.id">{{formatUserLabel(user)}}</option>
                </select>
              </div>
              <div class="col-12">
                <collapsible-notes-field v-model="lead.notes" id="lead-notes" rows="3" />
              </div>
            </div>
          </div>
          <div class="modal-footer border-0 pt-0">
            <button type="button" class="btn btn-light" data-bs-dismiss="modal">
              <AppIcon name="bi-x-lg" class="bi bi-x-lg me-1" />{{$t('close')}}
            </button>
            <button type="button" class="btn btn-success" :disabled="saving || !lead.name" @click="saveLead">
              <span v-if="saving" class="spinner-border spinner-border-sm me-2" role="status"></span>
              <AppIcon name="bi-check-lg" v-else class="bi bi-check-lg me-1" />
              {{ lead.id ? $t('update') : $t('save') }}
            </button>
          </div>
        </div>
      </div>
    </div>

    <!-- CSV Import Modal -->
    <div class="modal fade" id="import-modal" tabindex="-1" aria-hidden="true">
      <div class="modal-dialog modal-xl">
        <div class="modal-content">
          <div class="modal-header">
            <h5 class="modal-title">{{$t('import_leads')}}</h5>
            <button type="button" class="btn-close" data-bs-dismiss="modal" :disabled="importSaving"></button>
          </div>
          <div class="modal-body">
            <!-- Upload instructions -->
            <div class="alert alert-info d-flex align-items-center gap-3 mb-3">
              <AppIcon name="bi-info-circle-fill" class="bi bi-info-circle-fill" style="font-size:1.2rem;flex-shrink:0" />
              <div>
                {{$t('upload_csv_instruction')}}
                <button class="btn btn-link p-0 ms-1 align-baseline" @click="downloadTemplate" style="font-size:inherit">
                  <AppIcon name="bi-download" class="bi bi-download me-1" />{{$t('download_template_file')}}
                </button>
              </div>
            </div>

            <!-- File input -->
            <div class="mb-3">
              <label class="form-label fw-semibold">{{$t('file')}}</label>
              <input type="file" id="lead_file_upload" class="form-control" @change="handleFileUpload" accept=".xls,.xlsx,.csv" />
            </div>

            <!-- Preview table -->
            <div class="table-responsive mt-3" v-if="importLeads.length" style="max-height:400px">
              <div class="mb-2 d-flex align-items-center gap-2">
                <span class="badge bg-success">{{ importLeads.length }}</span>
                <span>{{$t('leads')}} {{$t('found')}}</span>
              </div>
              <table class="table mb-0 table-borderless table-striped small">
                <thead>
                  <tr class="text-secondary">
                    <th class="px-3">{{$t('name')}}<span class="text-danger">*</span></th>
                    <th class="px-3">{{$t('email')}}</th>
                    <th class="px-3">{{$t('phone')}}</th>
                    <th class="px-3">{{$t('company')}}</th>
                    <th class="px-3">{{$t('city')}}</th>
                    <th class="px-3">{{$t('address')}}</th>
                    <th class="px-3">{{$t('source')}}</th>
                    <th class="px-3">{{$t('value')}}</th>
                    <th class="px-3"></th>
                  </tr>
                </thead>
                <tbody>
                  <tr v-for="(item, i) in importLeads" :key="i">
                    <td class="px-3"><input type="text" v-model="importLeads[i].name" class="form-control form-control-sm" :class="{'border-danger': !importLeads[i].name}"></td>
                    <td class="px-3"><input type="text" v-model="importLeads[i].email" class="form-control form-control-sm"></td>
                    <td class="px-3"><input type="text" v-model="importLeads[i].phone" class="form-control form-control-sm"></td>
                    <td class="px-3"><input type="text" v-model="importLeads[i].company" class="form-control form-control-sm"></td>
                    <td class="px-3"><input type="text" v-model="importLeads[i].city" class="form-control form-control-sm"></td>
                    <td class="px-3"><input type="text" v-model="importLeads[i].address" class="form-control form-control-sm"></td>
                    <td class="px-3"><input type="text" v-model="importLeads[i].source" class="form-control form-control-sm"></td>
                    <td class="px-3"><input type="number" v-model="importLeads[i].value" class="form-control form-control-sm"></td>
                    <td class="px-3">
                      <button class="btn btn-sm btn-outline-danger p-1" @click="importLeads.splice(i, 1)" title="Remove row">
                        <AppIcon name="bi-trash" class="bi bi-trash" style="font-size:11px" />
                      </button>
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>
          <div class="modal-footer" v-if="importLeads.length">
            <button class="btn btn-light" data-bs-dismiss="modal" :disabled="importSaving">{{$t('close')}}</button>
            <button class="btn btn-success" @click="saveBulkLeads" :disabled="importSaving || !importLeads.some(l => l.name)">
              <span v-if="importSaving" class="spinner-border spinner-border-sm me-2"></span>
              <AppIcon name="bi-check-circle" v-else class="bi bi-check-circle me-2" />
              {{$t('import')}} {{ importLeads.length }} {{$t('leads')}}
            </button>
          </div>
        </div>
      </div>
    </div>



  </div>
</template>

<script>
import helper from '../../helper'
import { read, utils, writeFile } from 'xlsx'
import sortableList from '../../mixins/sortableList'
import { getLeadTableColumnDefaults } from '../../constants/leadTableColumns'

const LEAD_LIST_STATE_KEY = 'gstocky.leads.list-state'
const LEAD_SORT_FIELDS = [
  'reference',
  'name',
  'email',
  'phone',
  'company',
  'city',
  'address',
  'source',
  'status',
  'assigned_to',
  'value',
  'created_at',
  'updated_at'
]
const LEAD_DATE_RANGE_OPTIONS = ['', 'today', 'yesterday', 'this_week', 'last_week', 'this_month', 'custom']

export default {
  name: 'Lead',
  mixins: [sortableList],
  data() {
    return {
      search: '',
      leads: [],
      leadReminders: {},
      count: 0,
      page: 1,
      per_page: 25,
      loading: false,
      saving: false,
      importSaving: false,
      exporting: false,
      bulkAssigning: false,
      bulkAssigneeId: '',
      bulkStatusUpdating: false,
      bulkStatus: '',
      statusUpdatingLeadId: null,
      sourceUpdatingLeadId: null,
      showFilters: false,
      searchTimeout: null,
      restoringListState: false,
      statuses: [],
      users: [],
      activeFilters: [],
      availableFilters: [
        { key: 'name' },
        { key: 'status' },
        { key: 'assigned_to', param: 'user_id' },
        { key: 'source' },
        { key: 'company' },
        { key: 'email' },
        { key: 'phone' },
        { key: 'city' },
        { key: 'address' },
      ],
      visibleColumns: getLeadTableColumnDefaults(),
      dateRangeOption: '',
      range: [],
      start: null,
      end: null,
      isSelectedItem: false,
      lead: {
        id: null,
        name: '',
        email: '',
        phone: '',
        company: '',
        city: '',
        address: '',
        source: '',
        status: 'new',
        value: 0,
        notes: '',
        user_id: null,
      },
      importLeads: [],
      duplicateRowsRemoved: 0,
      leadModal: null,
      importModal: null,
    }
  },
  computed: {
    business() {
      return this.$store.getters.BUSINESS;
    },
    helper() {
      return helper;
    },
    leadRules() {
      const configured = this.business?.metadata?.lead_rules || {};
      return {
        phone_format: ['as_entered', 'digits_only', 'international'].includes(configured.phone_format)
          ? configured.phone_format
          : 'as_entered',
        phone_country_code: configured.phone_country_code || '',
        unique_fields: Array.isArray(configured.unique_fields) ? configured.unique_fields : ['email', 'phone']
      };
    },
    configuredLeadSources() {
      const sources = this.business?.metadata?.lead_sources;
      if (!Array.isArray(sources)) return [];
      return [...new Set(sources.map(source => String(source || '').trim()).filter(Boolean))];
    },
    formSourceOptions() {
      const sources = [
        ...this.configuredLeadSources,
        this.lead.source
      ].map(source => String(source || '').trim()).filter(Boolean);
      return [...new Set(sources)];
    },
    filterSourceOptions() {
      const sources = [
        ...this.configuredLeadSources,
        ...this.leads.map(lead => lead.source)
      ].map(source => String(source || '').trim()).filter(Boolean);
      return [...new Set(sources)];
    },
    selectableFilters() {
      return this.availableFilters.filter(f => !this.activeFilters.some(af => af.key === f.key));
    },
    isFiltered() {
      return (this.activeFilters.length > 0) || 
             (this.search && this.search.length > 0) ||
             (this.dateRangeOption && this.dateRangeOption !== '') ||
             (this.range && this.range.length > 0);
    },
    areAllSelected() {
      return this.leads.length > 0 && this.leads.every(l => l.selected);
    },
    selectedCount() {
      return this.leads.filter(l => l.selected).length;
    },
    totalPages() {
      return Math.ceil(
        Number(this.count || 0) / Number(this.per_page || 1)
      );
    },
    visiblePagination() {
      const total = this.totalPages;
      const current = Number(this.page) || 1;

      if (total <= 7) {
        return Array.from({ length: total }, (_, i) => i + 1);
      }

      if (current <= 4) {
        return [1, 2, 3, 4, 5, '...', total];
      }

      if (current >= total - 3) {
        return [
          1,
          '...',
          total - 4,
          total - 3,
          total - 2,
          total - 1,
          total
        ];
      }

      return [
        1,
        '...',
        current - 1,
        current,
        current + 1,
        '...',
        total
      ];
    }
  },
  watch: {
    search() {
      this.saveListState();
      if (this.restoringListState) return;
      this.debouncedSearch();
    },
    per_page() {
      this.getLeads(1);
    },
    range: {
      deep: true,
      handler() {
        if (this.restoringListState) return;

        if (this.range[0] == null) {
          this.start = null
          this.end = null
          this.saveListState()
          this.getLeads()
        } else if (this.range.length && this.range[0] && this.range[1]) {
          // eslint-disable-next-line no-undef
          this.start = moment(this.range[0]).format('YYYY-MM-DD')
          // eslint-disable-next-line no-undef
          this.end = moment(this.range[1]).format('YYYY-MM-DD')
          this.saveListState()
          this.getLeads()
        }
      }
    },
    activeFilters: {
      deep: true,
      handler() {
        this.saveListState();
      }
    },
    dateRangeOption() {
      this.saveListState();
    },
    sortField() {
      this.saveListState();
    },
    sortDirection() {
      this.saveListState();
    }
  },
  mounted() {
    window.mixpanel.track('leads');
    if(helper.checkAccess('view_leads') == -1) return this.$router.push({path : '/'})
    if(helper.checkAccess('view_leads') == -2) return this.$router.push({path : '/subscribe'})
    this.restoreListState();
    this.loadStatuses();
    this.loadAssignees();
    this.loadColumnSettings();
    this.getLeads();
    window.addEventListener('reminders:changed', this.loadLeadReminders);
    // eslint-disable-next-line no-undef
    this.leadModal = new bootstrap.Modal(document.getElementById('lead-modal'), { backdrop: 'static', keyboard: false });
    // eslint-disable-next-line no-undef
    this.importModal = new bootstrap.Modal(document.getElementById('import-modal'));
  },
  beforeDestroy() {
    window.removeEventListener('reminders:changed', this.loadLeadReminders);
  },
  methods: {
    listStateStorageKey() {
      const businessId = this.business?.id || 'default'
      const userId = this.$store.getters.USER?.id || 'default'
      return `${LEAD_LIST_STATE_KEY}.${businessId}.${userId}`
    },
    sortStateStorageKey() {
      return `${this.listStateStorageKey()}.sorting`
    },
    saveListState() {
      if (this.restoringListState) return

      try {
        const range = (this.range || []).map(value => {
          const date = value instanceof Date ? value : new Date(value)
          return Number.isNaN(date.getTime()) ? null : date.toISOString()
        }).filter(Boolean)

        localStorage.setItem(this.listStateStorageKey(), JSON.stringify({
          search: this.search,
          activeFilters: this.activeFilters,
          dateRangeOption: this.dateRangeOption,
          range,
        }))
      } catch (e) { /* localStorage may be unavailable */ }

      try {
        sessionStorage.setItem(this.sortStateStorageKey(), JSON.stringify({
          sortField: this.sortField,
          sortDirection: this.sortDirection,
        }))
      } catch (e) { /* sessionStorage may be unavailable */ }
    },
    restoreSortState() {
      try {
        const stored = JSON.parse(sessionStorage.getItem(this.sortStateStorageKey()) || 'null')
        this.sortField = stored && LEAD_SORT_FIELDS.includes(stored.sortField) ? stored.sortField : 'created_at'
        this.sortDirection = stored && ['asc', 'desc'].includes(stored.sortDirection) ? stored.sortDirection : 'desc'
      } catch (e) {
        this.sortField = 'created_at'
        this.sortDirection = 'desc'
      }
    },
    restoreListState() {
      this.restoringListState = true
      this.restoreSortState()

      try {
        const stored = JSON.parse(localStorage.getItem(this.listStateStorageKey()) || 'null')
        if (!stored || typeof stored !== 'object') return

        // Sorting used to live in this persistent record. Remove it during migration.
        delete stored.sortField
        delete stored.sortDirection
        localStorage.setItem(this.listStateStorageKey(), JSON.stringify(stored))

        const availableFiltersByKey = this.availableFilters.reduce((filters, filter) => {
          filters[filter.key] = filter
          return filters
        }, {})

        this.search = typeof stored.search === 'string' ? stored.search : ''
        this.activeFilters = Array.isArray(stored.activeFilters)
          ? stored.activeFilters.reduce((filters, filter) => {
            const availableFilter = filter && availableFiltersByKey[filter.key]
            if (!availableFilter || filters.some(item => item.key === availableFilter.key)) return filters

            filters.push({
              key: availableFilter.key,
              ...(availableFilter.param ? { param: availableFilter.param } : {}),
              value: filter.value == null ? '' : String(filter.value),
            })
            return filters
          }, [])
          : []

        this.dateRangeOption = LEAD_DATE_RANGE_OPTIONS.includes(stored.dateRangeOption) ? stored.dateRangeOption : ''

        if (this.dateRangeOption === 'custom') {
          const restoredRange = Array.isArray(stored.range)
            ? stored.range.map(value => new Date(value)).filter(date => !Number.isNaN(date.getTime()))
            : []
          this.range = restoredRange.length === 2 ? restoredRange : []
        } else {
          this.range = this.getDateRange(this.dateRangeOption)
        }

        if (this.range.length === 2) {
          // eslint-disable-next-line no-undef
          this.start = moment(this.range[0]).format('YYYY-MM-DD')
          // eslint-disable-next-line no-undef
          this.end = moment(this.range[1]).format('YYYY-MM-DD')
        } else {
          this.start = null
          this.end = null
        }
      } catch (e) {
        this.search = ''
        this.activeFilters = []
        this.dateRangeOption = ''
        this.range = []
        this.start = null
        this.end = null
      } finally {
        this.$nextTick(() => {
          this.restoringListState = false
        })
      }
    },
    loadColumnSettings() {
      this.visibleColumns = {
        ...getLeadTableColumnDefaults(),
        ...(this.business?.metadata?.leads_table_columns || {})
      };
    },
    formatMoney(n) { return helper.formatMoney(n); },
    formatDate(d) { return helper.formatDate(d); },
    formatDateTime(d) { return helper.formatDateTime(d); },
    formatUserLabel(user) {
      if (!user) return this.$t('unassigned');
      return user.name || user.email || '';
    },
    getLeadReminder(leadId) {
      return this.leadReminders[leadId] || null;
    },
    reminderUrgency(reminder) {
      if (!reminder || !reminder.remind_at) return 'scheduled';
      const remaining = new Date(reminder.remind_at).getTime() - Date.now();
      if (remaining <= 0) return 'due';
      if (remaining <= 24 * 60 * 60 * 1000) return 'imminent';
      if (remaining <= 3 * 24 * 60 * 60 * 1000) return 'soon';
      return 'scheduled';
    },
    leadReminderClass(lead) {
      const reminder = this.getLeadReminder(lead.id);
      return reminder ? `lead-reminder-row lead-reminder-row--${this.reminderUrgency(reminder)}` : '';
    },
    formatReminderRelative(value) {
      return helper.formatDateFromNow(value);
    },
    reminderTitle(reminder) {
      const note = String(reminder.note || '').slice(0, 120);
      return `${this.$t('reminder')}: ${helper.formatDateTime(reminder.remind_at)}${note ? ` — ${note}` : ''}`;
    },
    async loadLeadReminders() {
      const entityIds = this.leads.map(lead => lead.id);
      if (!entityIds.length) {
        this.leadReminders = {};
        return;
      }

      try {
        const params = new URLSearchParams({
          entity_type: 'lead',
          entity_ids: entityIds.join(','),
          status: 'scheduled',
          limit: '500',
        });
        const response = await helper.customGet(`${process.env.VUE_APP_API}/reminders?${params.toString()}`);
        const reminderMap = {};
        for (const reminder of response.data.data || []) {
          if (!reminderMap[reminder.entity_id]) reminderMap[reminder.entity_id] = reminder;
        }
        this.leadReminders = reminderMap;
      } catch (e) {
        this.leadReminders = {};
      }
    },

    async loadAssignees() {
      try {
        const res = await helper.customGet(`${process.env.VUE_APP_API}/leads/assignees`);
        this.users = res.data || [];
      } catch (e) { /* ignore */ }
    },

    // Statuses
    async loadStatuses() {
      try {
        const res = await helper.customGet(`${process.env.VUE_APP_API}/lead-statuses`);
        this.statuses = res.data;
      } catch (e) { /* ignore */ }
    },
    getStatusName(status) {
      const s = this.statuses.find(s => s.name === status);
      return s ? s.name : status || '';
    },
    getStatusColor(status) {
      const s = this.statuses.find(s => s.name === status);
      return s ? s.color : '#6c757d';
    },
    leadSourceOptions(lead) {
      const sources = [...this.configuredLeadSources, lead?.source]
        .map(source => String(source || '').trim())
        .filter(Boolean);
      return [...new Set(sources)];
    },
    async changeLeadSource(lead, source) {
      if (this.sourceUpdatingLeadId === lead.id || (lead.source || '') === source) return;

      const toggle = document.getElementById(`lead-source-${lead.id}`);
      if (toggle) {
        // eslint-disable-next-line no-undef
        const dropdown = bootstrap.Dropdown.getInstance(toggle);
        if (dropdown) dropdown.hide();
      }

      const previousSource = lead.source || '';
      this.sourceUpdatingLeadId = lead.id;
      this.$set(lead, 'source', source);
      try {
        const response = await helper.customPatch(
          `${process.env.VUE_APP_API}/leads/${lead.id}`,
          { source },
          { showToast: true }
        );
        if (response.data?.updated_at) this.$set(lead, 'updated_at', response.data.updated_at);
      } catch (e) {
        this.$set(lead, 'source', previousSource);
      } finally {
        this.sourceUpdatingLeadId = null;
      }
    },
    isLeadStatusDropdownDisabled(lead) {
      return this.statusUpdatingLeadId === lead.id;
    },
    async changeLeadStatus(lead, status) {
      if (this.isLeadStatusDropdownDisabled(lead) || lead.status === status) return;

      const toggle = document.getElementById(`lead-status-${lead.id}`);
      if (toggle) {
        // eslint-disable-next-line no-undef
        const dropdown = bootstrap.Dropdown.getInstance(toggle);
        if (dropdown) dropdown.hide();
      }

      const previousStatus = lead.status;
      this.statusUpdatingLeadId = lead.id;
      this.$set(lead, 'status', status);

      try {
        const payload = {
          name: lead.name,
          email: lead.email,
          phone: lead.phone,
          company: lead.company,
          city: lead.city,
          address: lead.address,
          source: lead.source,
          status,
          value: lead.value,
          notes: lead.notes,
        };

        await helper.customPatch(
          `${process.env.VUE_APP_API}/leads/${lead.id}`,
          payload,
          { showToast: true }
        );
      } catch (e) {
        this.$set(lead, 'status', previousStatus);
      } finally {
        await this.getLeads(this.page);
        this.statusUpdatingLeadId = null;
      }
    },
    // CRUD
    create() {
      this.lead = { id: null, name: '', email: '', phone: '', company: '', city: '', address: '', source: '', status: this.statuses.length ? this.statuses[0].name : 'new', value: 0, notes: '', user_id: null };
      this.leadModal.show();
    },
    editRow(row) {
      this.lead = { ...row, user_id: row.user_id || null };
      this.leadModal.show();
    },
    async saveLead() {
      try {
        this.saving = true;
        const payload = JSON.parse(JSON.stringify(this.lead));
        delete payload.id;
        delete payload.reference;
        delete payload.user;
        delete payload.created_at;
        delete payload.updated_at;
        delete payload.deleted_at;
        delete payload.selected;

        if (this.lead.id) {
          await helper.customPatch(`${process.env.VUE_APP_API}/leads/${this.lead.id}`, payload);
        } else {
          await helper.customPost(`${process.env.VUE_APP_API}/leads`, payload, { showToast: true });
        }
        this.saving = false;
        this.leadModal.hide();
        this.getLeads();
      } catch (e) {
        this.saving = false;
      }
    },
    async deleteRow(row) {
      const result = await this.$swal.fire({
        title: `${this.$t('are_you_sure')}?`,
        text: this.$t('you_cant_revert'),
        icon: 'warning',
        showCancelButton: true,
        confirmButtonColor: '#3085d6',
        cancelButtonColor: '#d33',
        confirmButtonText: this.$t('yes_delete_it')
      });
      if (result.isConfirmed) {
        await helper.customDelete(`${process.env.VUE_APP_API}/leads/${row.id}`);
        this.leads = helper.withoutDeletedRecords(this.leads, row.id);
        this.count = Math.max(0, this.count - 1);
      }
    },
    goDetails(id) {
      this.$router.push({ path: `/leads/${id}` });
    },

    // List
    buildLeadParams(page = 1, perPage = this.per_page) {
      const params = new URLSearchParams();
      params.append('page', page);
      params.append('per_page', perPage);
      this.appendSortParams(params);
      if (this.search) params.append('keyword', this.search);
      if (this.start) {
        params.append('start', this.start);
        params.append('end', this.end);
      }
      for (const filter of this.activeFilters) {
        if (filter.value && filter.value.trim()) params.append(filter.param || filter.key, filter.value.trim());
      }
      return params;
    },
    async getLeads(page) {
      try {
        this.loading = true;
        this.page = Number(page) || 1;

        const params = this.buildLeadParams(this.page, this.per_page);
        const url = `${process.env.VUE_APP_API}/leads?${params.toString()}`;
        const response = await helper.customGet(url);

        this.leads = response.data.data || [];

        // Add selected property to each lead
        this.leads.forEach(l => {
          this.$set(l, 'selected', false);
        });

        await this.loadLeadReminders();

        this.count = Number(response.data.count) || 0;

        // Keep the current page valid if data changed after delete/filtering.
        const maxPage = Math.max(1, Math.ceil(this.count / Number(this.per_page || 1)));
        if (this.page > maxPage) {
          this.loading = false;
          return this.getLeads(maxPage);
        }

        this.loading = false;
      } catch (e) {
        this.loading = false;
      }
    },
    debouncedSearch() {
      if (this.searchTimeout) clearTimeout(this.searchTimeout);
      this.searchTimeout = setTimeout(() => { this.getLeads(); }, 500);
    },

    // Filters
    addFilter(filter) {
        if (!this.isFilterActive(filter.key)) {
            const options = this.getFilterOptions(filter.key);
            const value = options.length ? options[0].value : '';
            this.activeFilters.push({ key: filter.key, param: filter.param, value });
        }
    },
    removeFilter(index) {
      this.activeFilters.splice(index, 1);
    },
    isFilterActive(key) {
        return this.activeFilters.some(f => f.key === key);
    },
    getFilterOptions(key) {
      if (key === 'status') {
        return this.statuses.map(status => ({
          value: status.name,
          label: status.name,
        }));
      }
      if (key === 'assigned_to') {
        return [
          ...this.users.map(user => ({ value: user.id, label: this.formatUserLabel(user) })),
          { value: 'unassigned', label: this.$t('unassigned') },
        ];
      }
      if (key === 'source') {
        return this.filterSourceOptions.map(source => ({ value: source, label: source }));
      }
      return [];
    },
    applyFilters() {
      this.getLeads();
    },
    clearAllFilters() {
      this.activeFilters = [];
      this.getLeads();
    },

    // Date range
    getDateRange(option) {
      const start = new Date()
      const end = new Date()

      if (option === 'today') return [start, end]
      if (option === 'yesterday') {
        start.setTime(start.getTime() - 3600 * 1000 * 24)
        end.setTime(end.getTime() - 3600 * 1000 * 24)
        return [start, end]
      }
      if (option === 'this_week') {
        start.setTime(start.getTime() - 3600 * 1000 * 24 * 7)
        return [start, end]
      }
      if (option === 'last_week') {
        start.setTime(start.getTime() - 3600 * 1000 * 24 * 14)
        end.setTime(end.getTime() - 3600 * 1000 * 24 * 7)
        return [start, end]
      }
      if (option === 'this_month') {
        start.setDate(1)
        return [start, end]
      }
      return []
    },
    handleDateRangeChange() {
      if (this.dateRangeOption === '') {
        this.range = []
        this.start = null
        this.end = null
      } else if (this.dateRangeOption === 'custom') {
        this.range = []
      } else {
        this.range = this.getDateRange(this.dateRangeOption)
      }
    },

    // Bulk selection
    toggleSelectAll() {
      const newState = !this.areAllSelected;
      this.leads.forEach(l => this.$set(l, 'selected', newState));
      this.changeSelectedItem();
    },
    changeSelectedItem() {
      this.isSelectedItem = this.leads.find(l => l.selected === true) ? true : false
    },
    async assignSelected() {
      const selectedIds = this.leads.filter(l => l.selected).map(l => l.id);
      if (selectedIds.length === 0 || this.bulkAssigneeId === '' || this.bulkAssigning) return;

      try {
        this.bulkAssigning = true;
        await helper.customPatch(
          `${process.env.VUE_APP_API}/leads/bulk/assignee`,
          { lead_ids: selectedIds, user_id: this.bulkAssigneeId },
          { showToast: true }
        );
        this.bulkAssigneeId = '';
        this.isSelectedItem = false;
        await this.getLeads(this.page);
      } catch (e) {
        /* handled by helper */
      } finally {
        this.bulkAssigning = false;
      }
    },
    async updateSelectedStatus() {
      const selectedIds = this.leads.filter(l => l.selected).map(l => l.id);
      if (selectedIds.length === 0 || this.bulkStatus === '' || this.bulkStatusUpdating) return;

      try {
        this.bulkStatusUpdating = true;
        await helper.customPatch(
          `${process.env.VUE_APP_API}/leads/bulk/status`,
          { lead_ids: selectedIds, status: this.bulkStatus },
          { showToast: true }
        );
        this.bulkStatus = '';
        this.isSelectedItem = false;
        await this.getLeads(this.page);
      } catch (e) {
        /* handled by helper */
      } finally {
        this.bulkStatusUpdating = false;
      }
    },
    async deleteSelected() {
      const selectedIds = this.leads.filter(l => l.selected).map(l => l.id);
      if (selectedIds.length === 0) return;

      const result = await this.$swal.fire({
        title: `${this.$t('are_you_sure')}?`,
        text: this.$t('you_cant_revert'),
        icon: 'warning',
        showCancelButton: true,
        confirmButtonColor: '#3085d6',
        cancelButtonColor: '#d33',
        confirmButtonText: this.$t('yes_delete_it')
      });

      if (result.isConfirmed) {
        this.loading = true;
        try {
          const results = await Promise.allSettled(selectedIds.map(id => helper.customDelete(`${process.env.VUE_APP_API}/leads/${id}`)));
          const deletedIds = helper.fulfilledRecordIds(selectedIds, results);
          this.leads = helper.withoutDeletedRecords(this.leads, deletedIds);
          this.count = Math.max(0, this.count - deletedIds.length);
          if (deletedIds.length !== selectedIds.length) throw results.find(result => result.status === 'rejected').reason;
          this.isSelectedItem = false;
        } catch (e) {
          /* ignore */
        } finally {
          this.loading = false;
        }
      }
    },

    // Export selected leads, or every lead matching the active filters.
    async getLeadsForExport() {
      const selectedLeads = this.leads.filter(lead => lead.selected);
      if (selectedLeads.length) return selectedLeads;

      const allLeads = [];
      const perPage = 1000;
      let currentPage = 1;
      let total = 0;

      do {
        const params = this.buildLeadParams(currentPage, perPage);
        const response = await helper.customGet(`${process.env.VUE_APP_API}/leads?${params.toString()}`);
        const pageLeads = response.data.data || [];
        total = Number(response.data.count) || 0;
        allLeads.push(...pageLeads);

        if (!pageLeads.length) break;
        currentPage += 1;
      } while (allLeads.length < total);

      return allLeads;
    },
    getLeadExportHeaders() {
      return [
        this.$t('reference'),
        this.$t('name'),
        this.$t('email'),
        this.$t('phone'),
        this.$t('company'),
        this.$t('city'),
        this.$t('address'),
        this.$t('source'),
        this.$t('status'),
        this.$t('value'),
        this.$t('notes'),
        this.$t('assigned_to'),
        this.$t('created_at'),
        this.$t('updated_at'),
      ];
    },
    getLeadExportRows(leads, csvSafe = false) {
      const safeText = value => {
        const text = value == null ? '' : String(value);
        return /^[=+\-@]/.test(text.trimStart()) ? `'${text}` : text;
      };
      const exportDate = value => {
        const date = value ? new Date(value) : null;
        if (!date || Number.isNaN(date.getTime())) return '';
        return csvSafe ? date.toISOString() : date;
      };

      return leads.map(lead => [
        safeText(lead.reference),
        safeText(lead.name),
        safeText(lead.email),
        safeText(lead.phone),
        safeText(lead.company),
        safeText(lead.city),
        safeText(lead.address),
        safeText(lead.source),
        safeText(this.getStatusName(lead.status)),
        Number(lead.value) || 0,
        safeText(lead.notes),
        safeText(lead.user ? (lead.user.name || lead.user.email) : ''),
        exportDate(lead.created_at),
        exportDate(lead.updated_at),
      ]);
    },
    downloadBlob(blob, filename) {
      const link = document.createElement('a');
      const url = URL.createObjectURL(blob);
      link.href = url;
      link.download = filename;
      link.style.display = 'none';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
    },
    async exportLeads(format) {
      if (this.exporting) return;

      try {
        this.exporting = true;
        const leads = await this.getLeadsForExport();
        if (!leads.length) return;

        const headers = this.getLeadExportHeaders();
        const dateSuffix = new Date().toISOString().slice(0, 10);

        if (format === 'csv') {
          const worksheet = utils.aoa_to_sheet([headers, ...this.getLeadExportRows(leads, true)]);
          const csv = utils.sheet_to_csv(worksheet);
          this.downloadBlob(
            new Blob([`\uFEFF${csv}`], { type: 'text/csv;charset=utf-8;' }),
            `leads_${dateSuffix}.csv`
          );
          return;
        }

        const worksheet = utils.aoa_to_sheet(
          [headers, ...this.getLeadExportRows(leads)],
          { cellDates: true }
        );
        worksheet['!cols'] = [
          { wch: 16 },
          { wch: 24 },
          { wch: 28 },
          { wch: 18 },
          { wch: 24 },
          { wch: 18 },
          { wch: 36 },
          { wch: 18 },
          { wch: 18 },
          { wch: 14 },
          { wch: 36 },
          { wch: 24 },
          { wch: 20 },
          { wch: 20 },
        ];
        worksheet['!autofilter'] = { ref: worksheet['!ref'] };

        for (let row = 2; row <= leads.length + 1; row += 1) {
          if (worksheet[`J${row}`]) worksheet[`J${row}`].z = '#,##0.00';
          if (worksheet[`M${row}`]) worksheet[`M${row}`].z = 'yyyy-mm-dd hh:mm';
        }

        const workbook = utils.book_new();
        utils.book_append_sheet(workbook, worksheet, 'Leads');
        writeFile(workbook, `leads_${dateSuffix}.xlsx`, { cellDates: true });
      } finally {
        this.exporting = false;
      }
    },

    // Template download — generates an XLSX file on the fly
    downloadTemplate() {
      const headers = [['name', 'email', 'phone', 'company', 'city', 'address', 'source', 'value']];
      const sampleRow = [['John Doe', 'john@example.com', '+1 555 000 0001', 'Acme Corp', 'New York', '123 Main St', 'Website', 5000]];
      const wsData = [...headers, ...sampleRow];
      const ws = utils.aoa_to_sheet(wsData);

      // Column widths
      ws['!cols'] = [
        { wch: 20 }, // name
        { wch: 25 }, // email
        { wch: 18 }, // phone
        { wch: 20 }, // company
        { wch: 18 }, // city
        { wch: 30 }, // address
        { wch: 15 }, // source
        { wch: 10 }, // value
      ];

      const wb = utils.book_new();
      utils.book_append_sheet(wb, ws, 'Leads');
      writeFile(wb, 'template_lead.xlsx');
    },

    // CSV Import
    showUpload() {
      this.importLeads = [];
      this.importSaving = false;
      this.duplicateRowsRemoved = 0;
      // Reset file input
      const fileInput = document.getElementById('lead_file_upload');
      if (fileInput) fileInput.value = '';
      this.importModal.show();
    },
    showImportToast(message, type = 'error') {
      if (this.$toast) {
        this.$toast.open({ message, type, position: 'bottom', duration: 7000 });
        return;
      }
      if (window.vueOpener) {
        window.vueOpener.open({ message, type, position: 'bottom', duration: 7000 });
      }
    },
    importLeadKey(value) {
      return value === null || value === undefined ? '' : String(value).trim().toLowerCase();
    },
    formatImportedPhone(value) {
      const phone = value === null || value === undefined ? '' : String(value).trim();
      if (!phone || this.leadRules.phone_format === 'as_entered') return phone;
      const digits = phone.replace(/\D/g, '');
      if (this.leadRules.phone_format === 'digits_only') return digits;
      if (!digits) return '';
      if (phone.startsWith('+')) return `+${digits}`;
      if (phone.startsWith('00')) return `+${digits.slice(2)}`;
      const countryDigits = String(this.leadRules.phone_country_code || '').replace(/\D/g, '');
      if (!countryDigits) return `+${digits}`;
      if (digits.startsWith(countryDigits)) return `+${digits}`;
      return `+${countryDigits}${digits.replace(/^0+/, '')}`;
    },
    importUniqueValue(field, lead) {
      return this.importLeadKey(field === 'phone' ? this.formatImportedPhone(lead[field]) : lead[field]);
    },
    deduplicateImportLeads(leads) {
      const supportedFields = ['name', 'email', 'phone', 'company'];
      const uniqueFields = this.leadRules.unique_fields.filter(field => supportedFields.includes(field));
      const uniqueValues = {};
      uniqueFields.forEach(field => { uniqueValues[field] = new Set(); });
      const fingerprints = new Set();
      const uniqueLeads = [];
      let duplicateCount = 0;

      leads.forEach(lead => {
        lead.phone = this.formatImportedPhone(lead.phone);
        const email = this.importLeadKey(lead.email);
        const phone = this.importLeadKey(lead.phone);
        const fingerprint = JSON.stringify([
          this.importLeadKey(lead.name),
          email,
          phone,
          this.importLeadKey(lead.company),
          this.importLeadKey(lead.city),
          this.importLeadKey(lead.address),
          this.importLeadKey(lead.source),
          Number(lead.value) || 0,
        ]);
        const isDuplicate = uniqueFields.some(field => {
          const value = this.importUniqueValue(field, lead);
          return value && uniqueValues[field].has(value);
        }) || fingerprints.has(fingerprint);

        if (isDuplicate) {
          duplicateCount += 1;
          return;
        }

        uniqueFields.forEach(field => {
          const value = this.importUniqueValue(field, lead);
          if (value) uniqueValues[field].add(value);
        });
        fingerprints.add(fingerprint);
        uniqueLeads.push(lead);
      });

      return { uniqueLeads, duplicateCount };
    },
    handleFileUpload(event) {
      const files = event && event.target ? event.target.files : [];
      if (!files.length) return;
      const filename = files[0].name;
      const extension = filename.substring(filename.lastIndexOf('.')).toUpperCase();
      if (extension === '.XLS' || extension === '.XLSX' || extension === '.CSV') {
        const reader = new FileReader();
        reader.onload = (e) => {
          try {
            const workbook = read(e.target.result, { type: 'array' });
            const importedRows = workbook.SheetNames.reduce((allRows, sheetName) => {
              const rows = utils.sheet_to_json(workbook.Sheets[sheetName], { defval: '' });
              const leads = rows.map(row => {
                const normalizedRow = Object.keys(row).reduce((result, key) => {
                  result[String(key).trim().toLowerCase()] = row[key];
                  return result;
                }, {});
                const value = parseFloat(normalizedRow.value);
                return {
                  name: normalizedRow.name ? String(normalizedRow.name).trim() : '',
                  email: normalizedRow.email ? String(normalizedRow.email).trim() : '',
                  phone: normalizedRow.phone ? String(normalizedRow.phone).trim() : '',
                  company: normalizedRow.company ? String(normalizedRow.company).trim() : '',
                  city: normalizedRow.city ? String(normalizedRow.city).trim() : '',
                  address: normalizedRow.address ? String(normalizedRow.address).trim() : '',
                  source: normalizedRow.source ? String(normalizedRow.source).trim() : '',
                  value: Number.isFinite(value) ? value : 0,
                };
              });
              return allRows.concat(leads);
            }, []);
            if (!importedRows.length) {
              this.importLeads = [];
              this.duplicateRowsRemoved = 0;
              this.showImportToast(this.$t('invalid_lead_import_file'));
              return;
            }
            const deduplicated = this.deduplicateImportLeads(importedRows);
            this.importLeads = deduplicated.uniqueLeads;
            this.duplicateRowsRemoved = deduplicated.duplicateCount;
            if (!this.importLeads.some(lead => lead.name)) {
              this.showImportToast(this.$t('lead_import_requires_name'));
            }
            if (deduplicated.duplicateCount) {
              this.showImportToast(
                this.$t('lead_duplicates_removed', { count: deduplicated.duplicateCount }),
                'info'
              );
            }
          } catch (error) {
            this.importLeads = [];
            this.duplicateRowsRemoved = 0;
            this.showImportToast(this.$t('invalid_lead_import_file'));
          }
        };
        reader.onerror = () => {
          this.importLeads = [];
          this.duplicateRowsRemoved = 0;
          this.showImportToast(this.$t('invalid_lead_import_file'));
        };
        reader.readAsArrayBuffer(files[0]);
      } else {
        this.importLeads = [];
        this.duplicateRowsRemoved = 0;
        if (event && event.target) event.target.value = '';
        this.showImportToast(this.$t('invalid_lead_import_file'));
      }
    },
    async saveBulkLeads() {
      const importedLeads = new Set();

      try {
        if (this.importSaving) return;
        // Only send rows with a name
        const validLeads = this.importLeads.filter(l => l.name && l.name.trim());
        if (!validLeads.length) {
          this.showImportToast(this.$t('lead_import_requires_name'));
          return;
        }

        const deduplicated = this.deduplicateImportLeads(validLeads);
        const leadsToImport = deduplicated.uniqueLeads;
        let duplicateCount = this.duplicateRowsRemoved + deduplicated.duplicateCount;
        let importedCount = 0;

        this.importSaving = true;
        const batchSize = 500;
        for (let start = 0; start < leadsToImport.length; start += batchSize) {
          const batch = leadsToImport.slice(start, start + batchSize);
          const response = await helper.customPost(`${process.env.VUE_APP_API}/leads/bulk`, { leads: batch });
          importedCount += Number(response.data.imported_count === undefined ? batch.length : response.data.imported_count) || 0;
          duplicateCount += Number(response.data.duplicate_count) || 0;
          batch.forEach(lead => importedLeads.add(lead));
        }
        this.importLeads = [];
        this.duplicateRowsRemoved = 0;
        this.importModal.hide();
        this.getLeads();
        const successMessage = duplicateCount
          ? this.$t('lead_import_summary', { imported: importedCount, duplicates: duplicateCount })
          : this.$t('leads_imported_successfully');
        this.$swal.fire({ icon: 'success', title: this.$t('success'), text: successMessage, timer: 2500, showConfirmButton: false });
      } catch (e) {
        // Keep only failed/unprocessed rows so retrying cannot duplicate completed batches.
        if (importedLeads.size) {
          this.importLeads = this.importLeads.filter(lead => !importedLeads.has(lead));
          this.getLeads();
        }
        // HTTP failures are toasted by the shared helper; cover local failures too.
        if (!e || !e.__toastShown) {
          this.showImportToast(e && e.message ? e.message : this.$t('something_went_wrong'));
        }
      } finally {
        this.importSaving = false;
      }
    }
  }
}
</script>

<style scoped>
.search-input {
  box-shadow: none !important;
}
.search-input:focus {
  box-shadow: none !important;
}
.action-btn {
  padding: 4px 8px;
  font-size: 12px;
}
.leads-list-table {
  min-height: 70vh;
  position: relative;
}
.leads-list-loading {
  position: absolute;
  inset: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  background: #fff;
}
.leads-list-empty {
  position: absolute;
  inset: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  text-align: center;
  background: #fff;
}
.leads-list-empty--onboarding {
  flex-direction: column;
}
.table-borderless>:not(caption)>*>*{
  padding: 5px !important
}

.lead-reminder-row > td {
  transition: background-color 0.2s ease, box-shadow 0.2s ease;
}

.lead-reminder-row > td:first-child {
  box-shadow: inset 4px 0 0 #6c8cff;
}

.lead-reminder-row--scheduled > td {
  background-color: #f3f6ff !important;
}

.lead-reminder-row--soon > td {
  background-color: #fff8db !important;
}

.lead-reminder-row--soon > td:first-child {
  box-shadow: inset 4px 0 0 #e0a800;
}

.lead-reminder-row--imminent > td {
  background-color: #fff0df !important;
}

.lead-reminder-row--imminent > td:first-child {
  box-shadow: inset 4px 0 0 #fd7e14;
}

.lead-reminder-row--due > td {
  background-color: #ffe8eb !important;
}

.lead-reminder-row--due > td:first-child {
  box-shadow: inset 4px 0 0 #dc3545;
}

.lead-reminder-badge {
  display: inline-flex;
  align-items: center;
  max-width: 180px;
  padding: 3px 7px;
  border-radius: 999px;
  font-size: 11px;
  font-weight: 600;
  white-space: nowrap;
  vertical-align: middle;
}

.lead-reminder-badge--scheduled {
  color: #3856b8;
  background: #e4eaff;
}

.lead-reminder-badge--soon {
  color: #856404;
  background: #ffe69c;
}

.lead-reminder-badge--imminent {
  color: #a44800;
  background: #ffd8ad;
}

.lead-reminder-badge--due {
  color: #a71d2a;
  background: #ffc9cf;
}

.lead-status-menu {
  min-width: max-content;
  padding: 2px 0;
}

.lead-status-menu .dropdown-item {
  padding: 3px 8px;
  font-size: 12px;
}

.lead-pagination {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 4px;
  padding: 16px;
  flex-wrap: wrap;
}

.pagination-btn {
  min-width: 38px;
  height: 38px;
  padding: 0 10px;
  border: 1px solid transparent;
  border-radius: 6px;
  background: transparent;
  font-size: 14px;
}

.pagination-btn:hover:not(:disabled) {
  background: #f5f5f7;
}

.pagination-btn.active {
  border-color: #94a3b8;
  color: #334155;
  background: #f7f7ff;
}

.pagination-btn:disabled {
  opacity: 0.35;
  cursor: not-allowed;
}

.pagination-ellipsis {
  min-width: 30px;
  text-align: center;
  color: #6c757d;
}

</style>
